import {
  Body,
  CanActivate,
  Controller,
  ExecutionContext,
  ForbiddenException,
  Get,
  Injectable,
  Module,
  Param,
  ParseEnumPipe,
  Put,
} from '@nestjs/common';
import { APP_GUARD, Reflector } from '@nestjs/core';
import { ApiBearerAuth, ApiTags } from '@nestjs/swagger';
import { Role } from '@sakura/prisma/enums';
import { ArrayUnique, IsArray, IsString } from 'class-validator';
import {
  CurrentUser,
  CurrentUserData,
  PERMISSIONS_KEY,
  Roles,
} from '../common/security';
import { PrismaService } from '../prisma/prisma.module';
import {
  ALL_PERMISSIONS,
  defaultPermissions,
  PERMISSION_CATALOG,
} from './permission-catalog';

class UpdateRolePermissionsDto {
  @IsArray()
  @ArrayUnique()
  @IsString({ each: true })
  permissions!: string[];
}

@Injectable()
export class RolePermissionsService {
  constructor(private readonly prisma: PrismaService) {}

  async forRole(role: Role): Promise<string[]> {
    if (role === Role.SUPER_ADMIN) {
      return [...ALL_PERMISSIONS];
    }
    const configured = await this.prisma.rolePermission.findUnique({
      where: { role },
    });
    return configured?.permissions ?? defaultPermissions(role);
  }

  async list() {
    return Promise.all(
      Object.values(Role).map(async (role) => ({
        role,
        permissions: await this.forRole(role),
        locked: role === Role.SUPER_ADMIN,
      })),
    );
  }

  async update(role: Role, permissions: string[]) {
    if (role === Role.SUPER_ADMIN) {
      throw new ForbiddenException(
        'Super Admin permissions are protected and cannot be reduced',
      );
    }
    const invalid = permissions.filter(
      (permission) => !ALL_PERMISSIONS.includes(permission),
    );
    if (invalid.length) {
      throw new ForbiddenException(`Unknown permissions: ${invalid.join(', ')}`);
    }
    return this.prisma.rolePermission.upsert({
      where: { role },
      update: { permissions },
      create: { role, permissions },
      select: { role: true, permissions: true, updatedAt: true },
    });
  }
}

@Injectable()
class PermissionsGuard implements CanActivate {
  constructor(
    private readonly reflector: Reflector,
    private readonly permissions: RolePermissionsService,
  ) {}

  async canActivate(context: ExecutionContext): Promise<boolean> {
    const required = this.reflector.getAllAndOverride<string[]>(
      PERMISSIONS_KEY,
      [context.getHandler(), context.getClass()],
    );
    if (!required?.length) {
      return true;
    }
    const request = context.switchToHttp().getRequest<{
      user?: CurrentUserData;
    }>();
    if (!request.user) {
      return false;
    }
    const granted = await this.permissions.forRole(request.user.role);
    return required.some((permission) => granted.includes(permission));
  }
}

@ApiTags('security')
@ApiBearerAuth()
@Controller('security')
export class SecurityController {
  constructor(private readonly permissions: RolePermissionsService) {}

  @Get('my-permissions')
  async myPermissions(@CurrentUser() user: CurrentUserData) {
    return { permissions: await this.permissions.forRole(user.role) };
  }

  @Get('catalog')
  @Roles(Role.SUPER_ADMIN)
  catalog() {
    return PERMISSION_CATALOG;
  }

  @Get('roles')
  @Roles(Role.SUPER_ADMIN)
  list() {
    return this.permissions.list();
  }

  @Put('roles/:role')
  @Roles(Role.SUPER_ADMIN)
  update(
    @Param('role', new ParseEnumPipe(Role)) role: Role,
    @Body() dto: UpdateRolePermissionsDto,
  ) {
    return this.permissions.update(role, dto.permissions);
  }
}

@Module({
  controllers: [SecurityController],
  providers: [
    RolePermissionsService,
    { provide: APP_GUARD, useClass: PermissionsGuard },
  ],
  exports: [RolePermissionsService],
})
export class SecurityModule {}
